import { FC, useMemo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { formatDate } from 'utils/helpers/formatters';
import { getStatusColor } from 'utils/helpers/work-trips';
import Loader from 'components/Loader';

export interface ILeaveData {
  [key: string]: string | number | any;
  id: number;
  user_id: number;
  user_name: string;
  date_from: string;
  date_to: string;
  status: string;
  comment?: string;
  type?: string;
  type_label?: string;
  sub_type?: string;
  leave_days?: number;
  part_of_day?: string | null;
  buttons?: { type: string; text: string }[];
  request_document_id?: number;
}

export interface IWorkTripData {
  [key: string]: string | number | any;
  id: number;
  user_id: number;
  user_name: string;
  date_from: string;
  date_to: string;
  status: string;
  destination: string;
  displayed_destination: string;
  route?: string;
  task: string;
  order_document_id: number;
  report_document_id?: number;
}

interface IEmployeeLeaveData {
  userId: number;
  userName: string;
  leaves: ILeaveData[];
  workTrips: IWorkTripData[];
}

interface IProps {
  leavesData: ILeaveData[];
  holidaysData: any[];
  workTripsData?: IWorkTripData[];
  date: Date;
  onDateChange?: (newDate: Date) => void;
  getLeaveColorForDay: (leaves: ILeaveData[], day: number, date: Date) => string | null;
  getLeaveTooltipContent: (leave: ILeaveData) => JSX.Element;
  renderCellActions?: (leave: ILeaveData, day: number, date: Date) => ReactNode;
  renderCellIcon?: (event: IWorkTripData) => ReactNode;
  isLoading?: boolean;
}

const LeavesCalendarGrid: FC<IProps> = ({
  leavesData,
  holidaysData,
  workTripsData,
  date,
  onDateChange,
  getLeaveColorForDay,
  getLeaveTooltipContent,
  renderCellActions,
  renderCellIcon,
  isLoading = false,
}) => {
  const { t } = useTranslation();

  const absenceTooltip = t('leaves.grid.onlyEmployeesWithAbsence');

  const goToBack = () => {
    const newDate = new Date(date);
    newDate.setMonth(newDate.getMonth() - 1);
    if (onDateChange) {
      onDateChange(newDate);
    }
  };

  const goToNext = () => {
    const newDate = new Date(date);
    newDate.setMonth(newDate.getMonth() + 1);
    if (onDateChange) {
      onDateChange(newDate);
    }
  };

  const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const daysArray = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const holidaysMap = useMemo(() => {
    const map = new Set<string>();
    holidaysData.forEach(holiday => {
      map.add(holiday.date);
    });
    return map;
  }, [holidaysData]);

  const isWeekend = (day: number): boolean => {
    const checkDate = new Date(date.getFullYear(), date.getMonth(), day);
    const dayOfWeek = checkDate.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  };

  const isHoliday = (day: number): boolean => {
    const dateStr = formatDate(new Date(date.getFullYear(), date.getMonth(), day), 'YYY-mm-dd');
    return holidaysMap.has(dateStr);
  };

  const employeeData = useMemo(() => {
    const employeesById: { [key: number]: IEmployeeLeaveData } = {};

    const getOrCreateEmployee = (userId: number, userName: string): IEmployeeLeaveData => {
      if (!employeesById[userId]) {
        employeesById[userId] = { userId, userName, leaves: [], workTrips: [] };
      }
      return employeesById[userId];
    };

    leavesData.forEach(leave => {
      getOrCreateEmployee(leave.user_id, leave.user_name).leaves.push(leave);
    });

    (workTripsData || []).forEach(workTrip => {
      getOrCreateEmployee(workTrip.user_id, workTrip.user_name).workTrips.push(workTrip);
    });

    return Object.values(employeesById).filter(emp => emp.leaves.length > 0 || emp.workTrips.length > 0);
  }, [leavesData, workTripsData]);

  const getLeaveForDay = (leaves: ILeaveData[], day: number): ILeaveData | null => {
    const currentDate = new Date(date.getFullYear(), date.getMonth(), day);
    currentDate.setHours(0, 0, 0, 0);

    const leave = leaves.find(l => {
      const leaveStart = new Date(l.date_from);
      leaveStart.setHours(0, 0, 0, 0);

      const leaveEnd = new Date(l.date_to);
      leaveEnd.setHours(0, 0, 0, 0);

      return currentDate >= leaveStart && currentDate <= leaveEnd;
    });

    return leave || null;
  };

  const getWorkTripForDay = (workTrips: IWorkTripData[], day: number): IWorkTripData | null => {
    const currentDate = new Date(date.getFullYear(), date.getMonth(), day);
    currentDate.setHours(0, 0, 0, 0);

    const workTrip = workTrips.find(trip => {
      const tripStart = new Date(trip.from_date);
      tripStart.setHours(0, 0, 0, 0);

      const tripEnd = new Date(trip.to_date);
      tripEnd.setHours(0, 0, 0, 0);

      return currentDate >= tripStart && currentDate <= tripEnd;
    });

    return workTrip || null;
  };

  const getWorkTripTooltipContent = (workTrip: any): JSX.Element => (
    <span>
      {t(`workTrips.statuses.${workTrip.status}`)}
      {workTrip.displayed_destination && (
        <>
          <br />
          {t('workTrips.destination')}: {workTrip.displayed_destination}
        </>
      )}
      {workTrip.route && (
        <>
          <br />
          {t('workTrips.route')}: {workTrip.route}
        </>
      )}
      {workTrip.task && (
        <>
          <br />
          {t('workTrips.task')}: {workTrip.task}
        </>
      )}
    </span>
  );

  return (
    <>
      <StyledCustomToolbar>
        <button
          type="button"
          onClick={goToBack}
        >
          <FaChevronLeft size={16} />
        </button>

        <h5 className="text-center">
          {t(`leaves.months.${date.getMonth() + 1}`)} {date.getFullYear()}
        </h5>

        <button
          type="button"
          onClick={goToNext}
        >
          <FaChevronRight size={16} />
        </button>
      </StyledCustomToolbar>

      {!isLoading && employeeData.length === 0 ? (
        <NoDataMessage>{t('leaves.grid.noEmployeesWithLeaves')}</NoDataMessage>
      ) : (
        <GridContainer $isLoading={isLoading}>
          <GridScrollWrapper>
            {isLoading ? (
              <LoaderWrapper>
                <Loader size="large" />
              </LoaderWrapper>
            ) : (
              <GridTable>
                <thead>
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="tooltip-grid-header">{absenceTooltip}</Tooltip>}
                  >
                    <HeaderRow>
                      <NameHeaderCell>{t('leaves.grid.employeeName')}</NameHeaderCell>
                      {daysArray.map(day => {
                        const isWeekendDay = isWeekend(day);
                        const isHolidayDay = isHoliday(day);

                        return (
                          <DayHeaderCell
                            key={day}
                            $isWeekend={isWeekendDay}
                            $isHoliday={isHolidayDay}
                          >
                            {day}
                          </DayHeaderCell>
                        );
                      })}
                    </HeaderRow>
                  </OverlayTrigger>
                </thead>
                <tbody>
                  {employeeData.map(employee => (
                    <EmployeeRow key={employee.userId}>
                      <NameCell>
                        <span className="d-flex align-items-center h-100">{employee.userName}</span>
                      </NameCell>
                      {daysArray.map(day => {
                        const leave = getLeaveForDay(employee.leaves, day);
                        const leaveColor = getLeaveColorForDay(employee.leaves, day, date);
                        const workTrip = getWorkTripForDay(employee.workTrips, day);
                        const workTripColor = workTrip ? getStatusColor(workTrip.status, true).backgroundColor : null;
                        const isWeekendDay = isWeekend(day);
                        const isHolidayDay = isHoliday(day);
                        const isGrayedOut = isWeekendDay || isHolidayDay;

                        const cellContent = (
                          <DayCell
                            key={day}
                            $backgroundColor={leaveColor || workTripColor}
                            $isGrayedOut={isGrayedOut}
                            $isWeekend={isWeekendDay}
                            $isHoliday={isHolidayDay}
                          >
                            <DayCellInner>
                              <DayCellContent>
                                {leave && renderCellActions && renderCellActions(leave, day, date)}
                                {workTrip && !leave && renderCellIcon && renderCellIcon(workTrip)}
                              </DayCellContent>
                            </DayCellInner>
                          </DayCell>
                        );

                        if (leave) {
                          return (
                            <OverlayTrigger
                              key={day}
                              placement="top"
                              overlay={
                                <Tooltip id={`tooltip-leave-${leave.id}-${day}`}>
                                  {getLeaveTooltipContent(leave)}
                                </Tooltip>
                              }
                            >
                              {cellContent}
                            </OverlayTrigger>
                          );
                        }

                        if (workTrip) {
                          return (
                            <OverlayTrigger
                              key={day}
                              placement="top"
                              overlay={
                                <Tooltip id={`tooltip-worktrip-${workTrip.id}-${day}`}>
                                  {getWorkTripTooltipContent(workTrip)}
                                </Tooltip>
                              }
                            >
                              {cellContent}
                            </OverlayTrigger>
                          );
                        }

                        return cellContent;
                      })}
                    </EmployeeRow>
                  ))}
                </tbody>
              </GridTable>
            )}
          </GridScrollWrapper>
        </GridContainer>
      )}
    </>
  );
};

export default LeavesCalendarGrid;

const StyledCustomToolbar = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;

  h5 {
    margin: 0;
    min-width: 10rem;
  }

  button {
    border: 0;
    background: none;
    opacity: 0.4;
    transition: 0.3s;

    svg {
      fill: #575962;
    }

    &:hover {
      opacity: 1;
      transition: 0.3s;
    }
  }
`;

const GridContainer = styled.div<{ $isLoading?: boolean }>`
  width: fit-content;
  max-width: 100%;
  border: ${props => (props.$isLoading ? 'none' : '1px solid #dee2e6')};
  border-radius: 4px;
  background: ${props => (props.$isLoading ? 'transparent' : 'white')};
  margin: 24px auto 0;
`;

const GridScrollWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  border-radius: inherit;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const GridTable = styled.table`
  border-collapse: separate;
  border-spacing: 0;
  width: max-content;
`;

const HeaderRow = styled.tr`
  background-color: #f8f9fa;
`;

const NameHeaderCell = styled.th`
  position: sticky;
  left: 0;
  z-index: 10;
  background-color: #f8f9fa;
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  border-bottom: 2px solid #dee2e6;
  border-right: 2px solid #dee2e6;
  width: 200px;
  min-width: 200px;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    width: 150px;
    min-width: 150px;
    max-width: 150px;
    font-size: 14px;
    padding: 8px 12px;
  }
`;

const DayHeaderCell = styled.th<{ $isWeekend: boolean; $isHoliday: boolean }>`
  padding: 12px 8px;
  text-align: center;
  font-weight: 600;
  border-bottom: 2px solid #dee2e6;
  border-right: 1px solid #dee2e6;
  min-width: ${props => (props.$isWeekend || props.$isHoliday ? '34px' : '40px')};
  max-width: ${props => (props.$isWeekend || props.$isHoliday ? '34px' : '40px')};
  background-color: #f8f9fa;
  color: #212529;

  @media (max-width: 1400px) and (min-width: 769px) {
    min-width: ${props => (props.$isWeekend || props.$isHoliday ? '28px' : '35px')};
    max-width: ${props => (props.$isWeekend || props.$isHoliday ? '28px' : '35px')};
  }

  @media (max-width: 768px) {
    min-width: ${props => (props.$isWeekend || props.$isHoliday ? '24px' : '30px')};
    max-width: ${props => (props.$isWeekend || props.$isHoliday ? '24px' : '30px')};
    font-size: 12px;
    padding: 8px 4px;
  }

  @media (min-width: 1725px) {
    min-width: ${props => (props.$isWeekend || props.$isHoliday ? '40px' : '45px')};
    max-width: ${props => (props.$isWeekend || props.$isHoliday ? '40px' : '45px')};
  }
`;

const EmployeeRow = styled.tr`
  &:hover {
    background-color: #f8f9fa;
  }

  &:last-child {
    td {
      border-bottom: none;
    }
  }
`;

const NameCell = styled.td`
  position: sticky;
  left: 0;
  z-index: 5;
  background-color: white;
  padding: 0 16px;
  border-bottom: 1px solid #dee2e6;
  border-right: 2px solid #dee2e6;
  font-weight: 500;
  width: 200px;
  min-width: 200px;
  max-width: 200px;
  height: 38px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${EmployeeRow}:hover & {
    background-color: #f8f9fa;
  }

  @media (max-width: 768px) {
    width: 150px;
    min-width: 150px;
    max-width: 150px;
    height: 30px;
    font-size: 14px;
    padding: 0 12px;
  }

  @media (min-width: 1725px) {
    height: 45px;
  }
`;

export const DayCell = styled.td<{
  $backgroundColor: string | null;
  $isGrayedOut: boolean;
  $isWeekend: boolean;
  $isHoliday: boolean;
}>`
  position: relative;
  width: ${props => (props.$isWeekend || props.$isHoliday ? '34px' : '40px')};
  min-width: ${props => (props.$isWeekend || props.$isHoliday ? '34px' : '40px')};
  border-bottom: 1px solid #dee2e6;
  border-right: 1px solid #dee2e6;
  cursor: ${props => (props.$backgroundColor ? 'pointer' : 'default')};
  background-color: ${props => {
    if (props.$backgroundColor) return props.$backgroundColor;
    if (props.$isGrayedOut) return '#ececec';
    return 'white';
  }};
  padding: 0;

  @media (max-width: 1400px) and (min-width: 769px) {
    width: ${props => (props.$isWeekend || props.$isHoliday ? '28px' : '35px')};
    min-width: ${props => (props.$isWeekend || props.$isHoliday ? '28px' : '35px')};
  }

  @media (max-width: 768px) {
    width: ${props => (props.$isWeekend || props.$isHoliday ? '24px' : '30px')};
    min-width: ${props => (props.$isWeekend || props.$isHoliday ? '24px' : '30px')};
  }

  @media (min-width: 1725px) {
    width: ${props => (props.$isWeekend || props.$isHoliday ? '40px' : '45px')};
    min-width: ${props => (props.$isWeekend || props.$isHoliday ? '40px' : '45px')};
  }
`;

export const DayCellInner = styled.div`
  position: relative;
  width: 100%;
  padding-top: 100%;
`;

export const DayCellContent = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #6c757d;
  font-size: 16px;
  margin-top: 24px;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  width: 100%;
`;
